import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Apollo, gql } from 'apollo-angular';
import { cloneDeep } from 'lodash-es';
import { map, of, Subscription, switchMap } from 'rxjs';
import { AuthorizationService } from '~ngx-shared/authentication';
import { ConfirmationService, MenuService } from '~ngx-shared/layout';
import { Aggregate } from '~ngx-shared/models';
import { QueryUtil } from '~ngx-shared/utils';
import {
  NotificationFolderModel,
  NotificationFolderTypeEnum,
  NotificationMessageModel,
  NotificationThreadModel
} from '../../models';
import { RoleEnum } from '../../utils';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly apollo = inject(Apollo);
  private readonly menuService = inject(MenuService);
  private readonly translocoService = inject(TranslocoService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly authorizationService = inject(AuthorizationService);

  readonly folders = signal<NotificationFolderModel[]>([]);
  readonly selectedFolder = signal<NotificationFolderModel | undefined>(undefined);

  readonly hasCustomFolders = computed(() => {
    const folders = this.folders();
    return folders?.some(item => item.type === NotificationFolderTypeEnum.CUSTOM);
  });

  readonly threads = signal<NotificationThreadModel[]>([]);
  readonly isLoadingThreads = signal(false);
  readonly threadsTotalCount = signal<number>(0);
  readonly selectedThread = signal<NotificationThreadModel | undefined>(undefined);

  readonly unreadMessageCountInInbox = signal<number | undefined>(undefined);
  readonly unreadMessageCountInArchive = signal<number | undefined>(undefined);
  readonly unreadMessageCountInTrash = signal<number | undefined>(undefined);
  readonly unreadMessageCountInCustom = signal<any>(undefined);

  readonly unreadMessageCount = computed(() => {
    const unreadMessageCountInInbox = this.unreadMessageCountInInbox();
    const unreadMessageCountInArchive = this.unreadMessageCountInArchive();
    const unreadMessageCountInTrash = this.unreadMessageCountInTrash();
    const unreadMessageCountInCustom = this.unreadMessageCountInCustom();

    return (
      (unreadMessageCountInInbox || 0) +
      (unreadMessageCountInArchive || 0) +
      (unreadMessageCountInTrash || 0) +
      (unreadMessageCountInCustom
        ? (Object.values(unreadMessageCountInCustom) as number[]).reduce((accumulator, value) => {
            return accumulator + value;
          }, 0)
        : 0)
    );
  });

  subscriptions: Subscription[] = [];

  constructor() {
    this.destroyRef.onDestroy(() => this.subscriptions.forEach(s => s.unsubscribe()));
    effect(() => {
      const count = this.unreadMessageCount();
      const menuItem = this.menuService.getItemFromRoute(['/', 'messages']);
      if (!menuItem) return;

      menuItem.badge = !!count ? String(count) : undefined;
    });
  }

  init() {
    if (this.authorizationService.can(RoleEnum.MODERATOR)) {
      return;
    }
    // Unsubscribe to clear old ones
    this.subscriptions.forEach(subscription => subscription.unsubscribe());

    this.subscriptions.push(
      this.apollo
        .subscribe<{
          result?: Aggregate;
        }>({
          query: gql`
            subscription GetNotificationThreadMessageCount {
              result: notification_thread_aggregate(
                where: {
                  thread_folders: { folder: { is_system: { _eq: true }, type: { _eq: inbox } } }
                  has_unread_messages: { _eq: true }
                }
              ) {
                aggregate {
                  count
                }
              }
            }
          `
        })
        .subscribe(queryResult => {
          this.unreadMessageCountInInbox.set(queryResult.data?.result?.aggregate.count);
        }),
      this.apollo
        .subscribe<{
          result?: Aggregate;
        }>({
          query: gql`
            subscription GetNotificationThreadMessageCount {
              result: notification_thread_aggregate(
                where: {
                  thread_folders: { folder: { is_system: { _eq: true }, type: { _eq: archive } } }
                  has_unread_messages: { _eq: true }
                }
              ) {
                aggregate {
                  count
                }
              }
            }
          `
        })
        .subscribe(queryResult => {
          this.unreadMessageCountInArchive.set(queryResult.data?.result?.aggregate.count);
        }),
      this.apollo
        .subscribe<{
          result?: Aggregate;
        }>({
          query: gql`
            subscription GetNotificationThreadMessageCount {
              result: notification_thread_aggregate(
                where: {
                  thread_folders: { folder: { is_system: { _eq: true }, type: { _eq: trash } } }
                  has_unread_messages: { _eq: true }
                }
              ) {
                aggregate {
                  count
                }
              }
            }
          `
        })
        .subscribe(queryResult => {
          this.unreadMessageCountInTrash.set(queryResult.data?.result?.aggregate.count);
        }),
      this.apollo
        .subscribe<{
          result?: NotificationFolderModel[];
        }>({
          query: gql`
            subscription GetNotificationThreadMessageCount {
              result: notification_folder(
                where: { is_system: { _eq: false }, type: { _eq: custom } }
              ) {
                id
                unread_messages_count
              }
            }
          `
        })
        .subscribe(queryResult => {
          // Map to object
          this.unreadMessageCountInCustom.set(
            queryResult.data?.result?.reduce((acc: any, folder) => {
              return {
                ...acc,
                [folder.id as number]: folder.unread_messages_count
              };
            }, this.unreadMessageCountInCustom())
          );
        })
    );
  }

  getFolders(fromCache = true) {
    if (fromCache && this.folders().length > 0) {
      return of(this.folders());
    }
    return this.apollo
      .query<{ result?: NotificationFolderModel[] }>({
        query: gql`
          query GetNotificationFolders {
            result: notification_folder(
              order_by: { is_system: desc_nulls_last, name: asc_nulls_first }
            ) {
              id
              is_system
              name
              type
            }
          }
        `
      })
      .pipe(
        map(queryResult => {
          this.folders.set(cloneDeep(queryResult.data?.result || []));
          return this.folders();
        })
      );
  }

  getThreads(
    offset = 0,
    limit: number | null | undefined = 10,
    filter?: {
      [p: string]: any;
    }[]
  ) {
    if (this.selectedFolder() && !this.isLoadingThreads()) {
      this.isLoadingThreads.set(true);
      return this.apollo
        .query<{ result?: NotificationThreadModel[]; aggregate?: Aggregate }>({
          query: gql`
            query GetNotificationThreads(
              $offset: Int
              $limit: Int
              $where: notification_thread_bool_exp
            ) {
              result: notification_thread(
                where: $where
                order_by: { last_message_at: desc_nulls_last }
                offset: $offset
                limit: $limit
              ) {
                id
                subject
                last_message_at
                message_count
                has_unread_messages
                latest_message: messages(limit: 1, order_by: { created_at: desc_nulls_last }) {
                  id
                  current_person_data {
                    person_id
                    academic_degree_prefix
                    academic_degree_suffix
                    first_name
                    last_name
                  }
                }
              }
              aggregate: notification_thread_aggregate(where: $where) {
                aggregate {
                  count
                }
              }
            }
          `,
          variables: {
            where: {
              _and: [
                ...(filter || []),
                { thread_folders: { folder_id: { _eq: this.selectedFolder()?.id } } },
                ...(this.selectedFolder()?.is_system &&
                this.selectedFolder()?.type === NotificationFolderTypeEnum.SENT
                  ? [{ _not: { parent: {} } }]
                  : [])
              ]
            },
            limit,
            offset
          }
        })
        .pipe(
          switchMap(queryResult => {
            const data = QueryUtil.dataOrThrow(queryResult);
            const totalCount = data.aggregate?.aggregate.count || 0;
            this.threadsTotalCount.set(totalCount);
            const threads = cloneDeep(data.result || []);
            this.threads.set(threads);
            this.isLoadingThreads.set(false);
            return of(threads);
          })
        );
    } else {
      return of([]);
    }
  }

  getThreadById(id?: number) {
    if (id) {
      return this.apollo
        .query<{ result?: NotificationThreadModel }>({
          query: gql`
            query GetNotificationThreadById($id: bigint!) {
              result: notification_thread_by_pk(id: $id) {
                id
                subject

                receiver_person_id
                sender_person_id

                children {
                  receiver_person_id
                }

                receiver_current_person_data {
                  person_id
                  academic_degree_prefix
                  academic_degree_suffix
                  first_name
                  last_name
                }

                sender_current_person_data {
                  person_id
                  academic_degree_prefix
                  academic_degree_suffix
                  first_name
                  last_name
                }

                messages(order_by: { created_at: asc_nulls_last }) {
                  id
                  message
                  created_at
                  receiver_read_at
                  person_id

                  message_documents {
                    id
                    size
                    name
                    file_id
                  }
                }
              }
            }
          `,
          variables: {
            id
          }
        })
        .pipe(
          switchMap(queryResult => {
            const thread = cloneDeep(QueryUtil.dataOrThrow(queryResult).result);
            this.selectedThread.set(thread);
            return of(thread);
          })
        );
    } else {
      return of(undefined);
    }
  }

  setMessageAsRead(message: NotificationMessageModel) {
    if (message && !message.receiver_read_at) {
      const ptr = setTimeout(() => {
        this.apollo
          .mutate<{ result: NotificationMessageModel }>({
            mutation: gql`
              mutation UpdateNotificationMessage($id: bigint!) {
                result: update_notification_message_by_pk(
                  pk_columns: { id: $id }
                  _set: { receiver_read_at: "now()" }
                ) {
                  id
                  receiver_read_at
                }
              }
            `,
            variables: {
              id: message.id
            }
          })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(mutationResult => {
            if (!mutationResult?.error && mutationResult.data?.result?.receiver_read_at) {
              // TODO: Update counts!
              message.receiver_read_at = mutationResult.data?.result?.receiver_read_at;
            }
          });

        clearTimeout(ptr);
      }, 1000);
    }
  }

  deleteThreads(threads?: (NotificationThreadModel | undefined)[], success?: Function) {
    if (threads?.length) {
      const folderId = this.folders().find(
        item => item.is_system && item.type === NotificationFolderTypeEnum.TRASH
      );
      if (folderId?.id) {
        this.confirmationService.confirmDeleteApollo({
          name:
            threads.length === 1
              ? threads[0]?.subject
              : this.translocoService.translate('shared.messages'),
          mutationOptions: {
            mutation: gql`
              mutation MoveThreadToSystemFolderById($threadIds: [bigint!], $folderId: bigint!) {
                result: update_notification_thread_folder(
                  where: { thread_id: { _in: $threadIds } }
                  _set: { folder_id: $folderId }
                ) {
                  affected_rows
                }
              }
            `,
            variables: {
              threadIds: threads.map(item => item?.id),
              folderId: folderId.id
            }
          },
          success
        });
      }
    }
  }

  archiveThreads(threads?: (NotificationThreadModel | undefined)[], success?: Function) {
    if (threads?.length) {
      const folderId = this.folders().find(
        item => item.is_system && item.type === NotificationFolderTypeEnum.ARCHIVE
      );
      if (folderId?.id) {
        this.moveThreads(
          threads.map(item => item?.id),
          folderId.id,
          success
        );
      }
    }
  }

  moveThreads(threadIds?: (number | undefined)[], folderId?: number, success?: Function) {
    if (threadIds?.length) {
      this.confirmationService.confirmApollo({
        mutationOptions: {
          mutation: gql`
            mutation MoveThreadToSystemFolderById($threadIds: [bigint!], $folderId: bigint!) {
              result: update_notification_thread_folder(
                where: { thread_id: { _in: $threadIds } }
                _set: { folder_id: $folderId }
              ) {
                affected_rows
              }
            }
          `,
          variables: {
            threadIds,
            folderId
          }
        },
        success
      });
    }
  }
}
